import type { ValidationMessage } from '../../gen/ejot/roofing/v1/types';

const CATEGORY_PRIORITY: readonly string[] = [
  'MESSAGE_CATEGORY_BLOCKING_INPUT',
  'MESSAGE_CATEGORY_MISSING_PRODUCT_DATA',
  'MESSAGE_CATEGORY_PRODUCT_INCOMPATIBILITY',
  'MESSAGE_CATEGORY_GEOMETRY',
  'MESSAGE_CATEGORY_UNSUPPORTED_SCOPE',
  'MESSAGE_CATEGORY_DATASET_VERSION',
  'MESSAGE_CATEGORY_ENGINEERING',
  'MESSAGE_CATEGORY_INSTALLATION',
  'MESSAGE_CATEGORY_REPORT',
];

/**
 * Selects one concise backend-authored message for a working panel.
 *
 * This is presentation prioritisation only. It does not infer engineering
 * readiness or replace the full ordered message list used for audit output.
 */
export function selectActionableDiagnostic(messages: readonly ValidationMessage[]): ValidationMessage | undefined {
  const populated = messages.filter((message) => Boolean(message?.title || message?.explanation));
  if (populated.length === 0) return undefined;

  for (const severity of ['SEVERITY_ERROR', 'SEVERITY_WARNING', 'SEVERITY_INFO'] as const) {
    const candidates = populated.filter((message) => message.severity === severity);
    if (candidates.length === 0) continue;
    for (const category of CATEGORY_PRIORITY) {
      const match = candidates.find((message) => message.category === category);
      if (match) return match;
    }
    return candidates[0];
  }
  return populated[0];
}

export function ActionableDiagnostic({
  messages,
  fallback,
}: {
  messages: readonly ValidationMessage[];
  fallback: string;
}) {
  const message = selectActionableDiagnostic(messages);
  if (!message) {
    return <p>{fallback}</p>;
  }

  const className = message.severity === 'SEVERITY_ERROR'
    ? 'message message-error'
    : message.severity === 'SEVERITY_WARNING'
      ? 'message message-warning'
      : 'message message-info';

  return (
    <div className={className} role={message.severity === 'SEVERITY_ERROR' ? 'alert' : 'status'}>
      <div className="message-title">{message.title || 'Calculation blocked'}</div>
      {message.explanation && <p className="message-body">{message.explanation}</p>}
      {message.required_action && (
        <p className="message-body"><strong>Required action:</strong> {message.required_action}</p>
      )}
    </div>
  );
}
